/* ---------- Hero: the claim beside the model itself, moving ---------- */
.hero {
  /* Same overlap as the home hero: cancel the flow space the sticky header reserves. */
  margin-top: calc(-1 * (var(--header-h) + 2 * var(--header-margin)));
  padding-block: calc(var(--header-h) + 2 * var(--header-margin) + var(--sp-12)) var(--sp-20);
  background: var(--surface-alt);
}
.heroGrid {
  display: grid;
  gap: var(--sp-12);
  align-items: center;
}
.heroText > * {
  max-width: 40rem;
}
.heroTitle {
  /* Room under the breadcrumb, which the dropped eyebrow label used to provide. */
  margin-top: var(--sp-6);
  font-size: clamp(2.4rem, 3.4vw + 1rem, 4.25rem);
  line-height: 1.04;
  letter-spacing: -0.035em;
  text-wrap: balance;
}
.heroLead {
  margin-top: var(--sp-5);
  font-size: var(--fs-lg);
  color: var(--text);
}
.heroActions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-4) var(--sp-8);
  margin-top: var(--sp-8);
}
.heroLink {
  font-weight: 600;
  color: var(--text-strong);
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 0.3em;
}
.heroLink:hover {
  color: var(--accent-text);
}
.heroNote {
  margin: var(--sp-6) 0 0;
  font-size: var(--fs-sm);
  color: var(--text-muted);
}
.heroNote + .heroNote {
  margin-top: var(--sp-2);
}
.heroMedia {
  position: relative;
}
.heroMedia > figure {
  box-shadow:
    0 0 0 1px var(--border),
    var(--shadow-lg);
}
/* One fact about the footage, pinned to it like a label. */
.heroChip {
  position: absolute;
  top: var(--sp-4);
  right: var(--sp-4);
  margin: 0;
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: var(--radius);
  background: rgba(8, 13, 19, 0.72);
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
  color: #f5f7fa;
  font-size: var(--fs-sm);
  font-weight: 600;
}
@media (min-width: 64rem) {
  .heroGrid {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.1fr);
    gap: var(--sp-16);
  }
}

/* ---------- The path: four numbered steps on hairline rows ---------- */
.steps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 15rem), 1fr));
  gap: var(--sp-8) var(--sp-10);
}
.steps li {
  padding-top: var(--sp-5);
  border-top: 1px solid var(--border-strong);
}
.stepNo {
  display: inline-grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  margin-bottom: var(--sp-4);
  border: 1px solid var(--accent);
  border-radius: 50%;
  font-weight: 700;
  color: var(--accent-text);
}
.steps h3 {
  margin-bottom: var(--sp-2);
  font-size: var(--fs-lg);
}
.steps p {
  margin: 0;
  font-size: var(--fs-sm);
  color: var(--text);
}
@media (min-width: 64rem) {
  .steps {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

/* ---------- Inside the model: the render beside what you can do in it ---------- */
.inside {
  display: grid;
  gap: var(--sp-10);
  align-items: center;
}
.insideImage {
  border-radius: var(--radius-lg);
  box-shadow:
    0 0 0 1px var(--border),
    var(--shadow-lg);
}
.insideText h2 {
  max-width: 18ch;
  text-wrap: balance;
}
.insideLead {
  max-width: 34rem;
  margin: var(--sp-5) 0 var(--sp-8);
  font-size: var(--fs-md);
  color: var(--text);
}
/* A checklist on hairline rows: what someone in the room can ask the model to do. */
.capabilities {
  display: grid;
}
.capabilities li {
  position: relative;
  padding: var(--sp-3) 0 var(--sp-3) var(--sp-8);
  border-top: 1px solid var(--border);
  color: var(--text);
}
.capabilities li:last-child {
  border-bottom: 1px solid var(--border);
}
.capabilities li::before {
  content: "";
  position: absolute;
  top: calc(var(--sp-3) + 0.45em);
  left: var(--sp-1);
  width: 0.7rem;
  height: 0.4rem;
  border-bottom: 2px solid var(--accent);
  border-left: 2px solid var(--accent);
  rotate: -45deg;
}
@media (min-width: 64rem) {
  .inside {
    grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
    gap: var(--sp-16);
  }
}

/* ---------- Outputs: by the form they take, one tab each ---------- */
.outputTabs {
  margin-bottom: var(--sp-8);
}
.outputPanel {
  display: grid;
  gap: var(--sp-8);
}
.outputMedia {
  margin: 0;
}
.outputImage {
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
}
.outputCaption {
  max-width: 46rem;
  margin-top: var(--sp-4);
  font-size: var(--fs-sm);
  color: var(--text-muted);
}
/* A spec sheet: hairline rows, each output's name over what it is. */
.outputList {
  display: grid;
  align-content: start;
}
.outputList li {
  padding-block: var(--sp-4);
  border-top: 1px solid var(--border-strong);
  color: var(--text);
}
.outputList li:last-child {
  border-bottom: 1px solid var(--border-strong);
}
.outputList strong {
  display: block;
  margin-bottom: 2px;
  font-size: var(--fs-md);
  color: var(--text-strong);
}
@media (min-width: 64rem) {
  .outputPanel {
    grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
    align-items: start;
    gap: var(--sp-16);
  }
}

/* ---------- Uses: six on hairline rows, three across ---------- */
.uses {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 19rem), 1fr));
  gap: var(--sp-10) var(--sp-12);
}
.use {
  padding-top: var(--sp-5);
  border-top: 1px solid var(--border-strong);
}
.useTitle {
  margin-bottom: var(--sp-3);
  font-size: var(--fs-xl);
  line-height: var(--lh-snug);
}
.use p {
  margin: 0;
  color: var(--text);
}
@media (min-width: 64rem) {
  .uses {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
